import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';
import { Check, Phone, Mail, MapPin, ArrowRight, Handshake, Star, ChevronRight } from 'lucide-react';
import { company, areas, gallery, testimonials, leaveReviewUrl, waterHeaterFaqs, drainFaqs, backflowFaqs, sewerLineFaqs, slabLeakFaqs, sameDayFaqs, waterFiltrationFaqs, newConstructionFaqs, renovationFaqs, largeProjectsFaqs, contractorFaqs } from '../mock';
import Seo from '../components/Seo';
import StructuredData from '../components/StructuredData';
import ServiceLeadForm from '../components/ServiceLeadForm';
import StickyBookBar from '../components/StickyBookBar';
import TradePartnerModal from '../components/TradePartnerModal';
import { PageFaq, useLiveReviews } from '../sections/RestSections';

const projectPhotos = gallery
  .filter((g) => ['new construction', 'commercial', 'repipe', 'remodel'].includes(g.tag))
  .slice(0, 4);

const LANDINGS = {
  'water-heater-repair-savannah': {
    eyebrow: 'Water Heaters',
    h1: 'Water Heater Repair & Installation in Savannah, GA',
    metaTitle: 'Water Heater Repair in Savannah, GA',
    metaDesc: 'Fast, licensed water heater repair & installation in Savannah, GA. Tank & tankless service from Victory Plumbing — family owned, Class II Master Plumber. Call (912) 429-9428.',
    image: '/img/water-heater.jpg',
    intro: 'No hot water? A leaking tank? Victory Plumbing repairs and installs both traditional tank and tankless water heaters across Savannah and the Coastal Empire. As a family-owned Class II Master Plumber (MP#210521), we diagnose the problem fast and give you honest, upfront options — so you get reliable hot water without the guesswork.',
    included: [
      'Tank & tankless water heater repair',
      'New water heater installation & replacement',
      'Leaking tank & pressure-relief valve fixes',
      'No-hot-water & pilot/ignition troubleshooting',
      'Sediment flushing & routine maintenance',
      'Gas & electric water heater service',
    ],
    signsTitle: 'Signs you may need water heater service',
    signs: [
      'Water never gets hot or runs out quickly',
      'Rusty, cloudy, or metallic-smelling hot water',
      'Puddles or moisture around the tank',
      'Popping or rumbling noises from the tank',
      'The unit is 10+ years old',
    ],
    faqs: waterHeaterFaqs,
  },
  'drain-cleaning-savannah': {
    eyebrow: 'Drains & Clogs',
    h1: 'Drain Cleaning & Clog Removal in Savannah, GA',
    metaTitle: 'Drain Cleaning in Savannah, GA',
    metaDesc: 'Professional drain cleaning & clog removal in Savannah, GA. Fast, affordable service from Victory Plumbing for sinks, tubs, toilets & main lines. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/nvdmqgmp_07%3A21%3A26.webp',
    intro: 'A clogged drain is more than a nuisance — left alone it can back up your whole home. Victory Plumbing clears clogs quickly and cost-effectively across Savannah and the surrounding areas, and we walk you through the cause so it doesn\u2019t keep happening. From a slow kitchen sink to a blocked main line, we\u2019ve got it covered.',
    included: [
      'Kitchen, bath & utility sink clogs',
      'Tub, shower & floor drain cleaning',
      'Toilet clogs & blockages',
      'Main line & sewer clearing',
      'Recurring & stubborn clog diagnosis',
      'Cleanout & camera inspection',
    ],
    signsTitle: 'Signs you may need a drain cleaning',
    signs: [
      'Water backing up in the sink or tub',
      'Water draining very slowly',
      'Gurgling drains after the washer or dishwasher',
      'Toilet water bubbling when the sink runs',
      'A rotting-food smell from the drain',
    ],
    faqs: drainFaqs,
  },
  'backflow-prevention-savannah': {
    eyebrow: 'Backflow Prevention',
    h1: 'Backflow Testing & Prevention in Savannah, GA',
    metaTitle: 'Backflow Prevention & Testing in Savannah, GA',
    metaDesc: 'GBPAT-certified backflow testing, certification, repair & installation in Savannah, GA. Protect your water supply with Victory Plumbing. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/23552w9l_07%3A20%3A26.2.webp',
    intro: 'Backflow prevention protects your drinking water from contamination \u2014 and annual testing is required for many homes and businesses. Victory Plumbing is GBPAT certified for backflow testing, certification, repair, and installation across Savannah and the Coastal Empire, and we make the whole process quick and hassle-free.',
    included: [
      'Annual backflow testing & certification',
      'Backflow preventer installation',
      'Repair & rebuild of existing assemblies',
      'Paperwork filed with your water authority',
      'Commercial & residential systems',
      'Irrigation & fire-line backflow devices',
    ],
    signsTitle: 'Why backflow prevention matters',
    signs: [
      'Required annually by many local authorities',
      'Protects your water from contamination',
      'Keeps your business code-compliant',
      'Avoids fines for missed certification',
      'Peace of mind for your family or tenants',
    ],
    faqs: backflowFaqs,
  },
  'sewer-line-repair-savannah': {
    eyebrow: 'Sewer & Water Lines',
    h1: 'Sewer & Water Line Repair in Savannah, GA',
    metaTitle: 'Sewer Line Repair in Savannah, GA',
    metaDesc: 'Expert sewer & water line repair, replacement & installation in Savannah, GA. Slab leaks, main lines & cleanouts by Victory Plumbing. Call (912) 429-9428.',
    image: '/img/pool-manifold.jpg',
    intro: 'From stubborn sewer backups to slab leaks and aging main lines, Victory Plumbing repairs, replaces, and installs water and sewer lines across Savannah and the surrounding areas \u2014 including tricky work on the historic city\u2019s older infrastructure. Fast, reliable, and priced fairly.',
    included: [
      'Sewer line repair & replacement',
      'Water main repair & installation',
      'Slab leak detection & repair',
      'Cleanout installation & clearing',
      'Camera inspection & line locating',
      'Trenchless options where available',
    ],
    signsTitle: 'Signs of a sewer or water line problem',
    signs: [
      'Multiple drains backing up at once',
      'Sewage smell in the yard or home',
      'Soggy or sunken spots in the lawn',
      'Unexplained spike in your water bill',
      'Gurgling toilets & slow drains throughout',
    ],
    faqs: sewerLineFaqs,
  },
  'slab-leak-detection-savannah': {
    eyebrow: 'Slab Leaks',
    h1: 'Slab Leak Detection & Repair in Savannah, GA',
    metaTitle: 'Slab Leak Detection & Repair in Savannah, GA',
    metaDesc: 'Fast, non-invasive slab leak detection & repair in Savannah, GA. Victory Plumbing locates and fixes leaks under your foundation \u2014 family owned, licensed. Call (912) 429-9428.',
    image: '/img/pool-manifold.jpg',
    intro: 'A leak under your concrete slab can quietly drive up your water bill and damage your foundation before you ever see a drop. Victory Plumbing uses electronic leak detection and acoustic equipment to pinpoint slab leaks fast \u2014 then repairs, reroutes, or repipes with as little disruption to your home as possible. Family-owned and a Class II Master Plumber (MP#210521), we give you honest options before any work begins.',
    included: [
      'Electronic & acoustic slab leak detection',
      'Pressure testing to confirm the leak',
      'Targeted spot repair of damaged pipe',
      'Line rerouting to avoid major demolition',
      'Full repipe when it\u2019s the smarter fix',
      'Foundation & water-line leak diagnosis',
    ],
    signsTitle: 'Signs you may have a slab leak',
    signs: [
      'An unexplained spike in your water bill',
      'The sound of running water when everything is off',
      'Warm or damp spots on the floor',
      'Low water pressure throughout the home',
      'Cracks in the foundation, walls, or flooring',
    ],
    faqs: slabLeakFaqs,
  },
  'same-day-plumbing-repair-savannah': {
    eyebrow: 'Same-Day Repair',
    h1: 'Same-Day Plumbing Repair in Savannah, GA',
    metaTitle: 'Same-Day Plumbing Repair in Savannah, GA',
    metaDesc: 'Need a plumber today? Victory Plumbing offers fast same-day plumbing repair in Savannah, GA \u2014 leaks, clogs, water heaters & more. Family owned, licensed. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/nvdmqgmp_07%3A21%3A26.webp',
    intro: 'When a plumbing problem can\u2019t wait, Victory Plumbing is ready. In most cases we can get to you the same day you call \u2014 diagnosing and fixing leaks, clogs, water heater failures, and running toilets across Savannah and the Coastal Empire. As a family-owned Class II Master Plumber, we show up on time, give you an honest estimate up front, and get your plumbing working again fast.',
    included: [
      'Burst & leaking pipe repair',
      'No-hot-water & water heater fixes',
      'Clogged drain & toilet clearing',
      'Running & overflowing toilet repair',
      'Faucet & fixture leak repair',
      'Fast diagnosis with honest, upfront pricing',
    ],
    signsTitle: 'When to call for same-day service',
    signs: [
      'A pipe is leaking, dripping, or has burst',
      'You have no hot water',
      'A drain or toilet is badly clogged',
      'Water is pooling or won\u2019t shut off',
      'A fixture failed and you need it working today',
    ],
    faqs: sameDayFaqs,
  },
  'water-filtration-savannah': {
    eyebrow: 'Water Filtration',
    h1: 'Water Filtration & Softener Systems in Savannah, GA',
    metaTitle: 'Water Filtration & Water Softeners in Savannah, GA',
    metaDesc: 'Cleaner, softer water for your home. Victory Plumbing installs & services whole-home water filtration and softener systems in Savannah, GA. Call (912) 429-9428.',
    image: '/img/pool-manifold.jpg',
    intro: 'Hard water and poor water quality can damage your pipes and appliances \u2014 and it\u2019s tough on your skin, hair, and dishes. Victory Plumbing installs, repairs, and maintains whole-home water filtration and softener systems across Savannah and the Coastal Empire, giving your family cleaner, better-tasting, softer water. As a family-owned Class II Master Plumber (MP#210521), we help you choose the right system and install it right the first time.',
    included: [
      'Whole-home water filtration installation',
      'Water softener installation & setup',
      'Reverse osmosis drinking-water systems',
      'Filter & media replacement',
      'System repair & maintenance',
      'Water testing & correct system sizing',
    ],
    signsTitle: 'Signs you may need water treatment',
    signs: [
      'Spotty dishes & soap scum buildup',
      'Dry skin & hair after showering',
      'Scale buildup on faucets & fixtures',
      'Bad taste, odor, or cloudy water',
      'Appliances wearing out quickly',
    ],
    faqs: waterFiltrationFaqs,
  },
  'new-construction': {
    eyebrow: 'New Construction',
    h1: 'New Construction Plumbing in Savannah, GA',
    metaTitle: 'New Construction Plumbing in Savannah, GA',
    metaDesc: 'Full-service new construction plumbing in Savannah, GA \u2014 rough-in to final inspection for homes & commercial builds. Victory Plumbing, licensed & insured. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/j0lckjy8_07%3A18%3A26.2.jpg',
    intro: 'Victory Plumbing handles new construction plumbing from underground and rough-in all the way through fixture set and final inspection \u2014 for both residential and commercial builds across Savannah and the Coastal Empire. As a family-owned Class II Master Plumber (MP#210521), we pull permits, follow a standardized safety and quality process, and coordinate with your general contractor and inspectors so your build stays on schedule and up to code.',
    included: [
      'Site plumbing layout & design input',
      'Underground & rough-in plumbing',
      'Water, gas & sewer line installation',
      'Fixture set & final trim-out',
      'Stand-alone plumbing permits pulled',
      'Coordination with GCs & city inspectors',
    ],
    signsTitle: 'Why builders choose Victory Plumbing',
    signs: [
      'Class II Master Plumber on every job',
      'Clear estimates before work begins',
      'Standardized safety & quality process',
      'Permits pulled and inspections handled',
      'Reliable scheduling that keeps builds moving',
    ],
    faqs: newConstructionFaqs,
  },
  'large-scale-renovations': {
    eyebrow: 'Renovations',
    h1: 'Large-Scale Renovation Plumbing in Savannah, GA',
    metaTitle: 'Renovation & Repipe Plumbing in Savannah, GA',
    metaDesc: 'Large-scale renovation & repipe plumbing in Savannah, GA. Victory Plumbing relocates lines, repipes buildings & restores historic structures \u2014 licensed & insured. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/j1ofpqsg_07%3A18%3A26.6.webp',
    intro: 'From whole-building repipes to relocating and reconfiguring lines, Victory Plumbing plans and executes large-scale renovations across Savannah and the Coastal Empire \u2014 including specialized work in the historic city\u2019s older structures. Our owner founded the company on plumbing restoration, so we advise on what\u2019s possible while preserving a building\u2019s character, keeping everything code-compliant with an eye on safety and minimal downtime.',
    included: [
      'Whole-building repipes',
      'Line relocation & reconfiguration',
      'Historic structure plumbing restoration',
      'Fixture & system upgrades',
      'Water, gas & sewer modifications',
      'Code compliance & permitting',
    ],
    signsTitle: 'What to expect on a renovation',
    signs: [
      'On-site evaluation of your existing system',
      'A clear plan for code, layout & cost',
      'A base estimate before work begins',
      'Experience with historic Savannah structures',
      'Coordination with other trades & inspectors',
    ],
    faqs: renovationFaqs,
  },
  'large-plumbing-projects': {
    eyebrow: 'Large Projects',
    h1: 'Large-Scale Plumbing Projects in Savannah, GA',
    metaTitle: 'Large Commercial & Industrial Plumbing in Savannah, GA',
    metaDesc: 'Large-scale commercial & industrial plumbing in Savannah, GA \u2014 lift stations, grease traps, industrial kitchens, backflow & phased installs. Victory Plumbing. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/smqk9f4i_07%3A18%3A26.webp',
    intro: 'Victory Plumbing delivers solutions for even the most complicated commercial and industrial jobs, built to keep your business or facility running. From lift stations and grease traps to industrial kitchens, fire hydrants, and backflow systems, our team plans, schedules, and executes large-scale projects \u2014 including multi-unit and phased installations \u2014 with the state-of-the-art expertise and customer focus Savannah businesses count on.',
    included: [
      'Lift stations & grease traps',
      'Industrial kitchens & baths',
      'Fire hydrants & backflow systems',
      'Multi-unit & phased installations',
      'Project planning & scheduling',
      'Commercial & industrial expertise',
    ],
    signsTitle: 'Built for complex jobs',
    signs: [
      'Solutions for the most complicated projects',
      'Phased scheduling to protect operations',
      'Class II Master Plumber & certified team',
      'Clear estimates on scope, code & timeline',
      'Coordination with your team & inspectors',
    ],
    faqs: largeProjectsFaqs,
  },
  'contractor-services': {
    eyebrow: 'For Contractors',
    h1: 'Plumbing Contractor Services in Savannah, GA',
    metaTitle: 'Plumbing Subcontractor for Builders in Savannah, GA',
    metaDesc: 'Reliable plumbing subcontractor services in Savannah, GA for GCs & builders \u2014 rough-ins, finish plumbing, permits & code-compliant work. Victory Plumbing. Call (912) 429-9428.',
    image: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_lowcountry-test/artifacts/s0thcs0i_07%3A18%3A26.4.jpg',
    intro: 'Victory Plumbing partners with general contractors and builders as a dependable plumbing subcontractor on residential and commercial projects across Savannah and the Coastal Empire. We provide reliable rough-in and finish plumbing, clear communication, and efficient scheduling that keeps your project moving \u2014 backed by a Class II Master Plumber, standardized safety procedures, and full licensing and insurance.',
    included: [
      'Sub-contract plumbing for GCs & builders',
      'New construction & remodel rough-ins',
      'Finish plumbing & fixture set',
      'Stand-alone plumbing permits',
      'Code-compliant installations',
      'Residential & commercial projects',
    ],
    signsTitle: 'Why contractors partner with us',
    signs: [
      'Reliable, on-time scheduling',
      'Clear communication throughout the job',
      'Licensed, insured & certified crews',
      'Standardized safety & quality process',
      'Permits and inspections handled',
    ],
    faqs: contractorFaqs,
  },
};

export default function ServiceLandingPage() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const [tpOpen, setTpOpen] = useState(false);
  const live = useLiveReviews();
  const base = pathname.startsWith('/commercial') ? '/commercial' : '/residential';
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  const data = LANDINGS[slug];
  if (!data) return <Navigate to={base} replace />;

  const sectionName = base === '/commercial' ? 'Construction' : 'Service';
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: sectionName, path: base },
    { name: data.eyebrow, path: `${base}/${slug}` },
  ];

  const liveReviews = (live && live.reviews) || [];
  const fiveStar = liveReviews.filter((r) => Math.round(r.stars || 5) >= 5);
  const reviewSource = fiveStar.length >= 3 ? fiveStar : (liveReviews.length ? liveReviews : testimonials);
  const reviewCards = reviewSource.slice(0, 3);
  const reviewsUrl = (live && live.google_maps_uri) || leaveReviewUrl;

  return (
    <main className="page services-page" data-testid="service-landing">
      <Seo title={data.metaTitle} description={data.metaDesc} path={`${base}/${slug}`} />
      <StructuredData business={false} service={{ name: data.h1, serviceType: data.eyebrow, description: data.metaDesc, path: `${base}/${slug}` }} breadcrumbs={breadcrumbs} />
      <section className="page-hero landing-hero">
        <div className="wrap">
          <div className="landing-hero-copy">
            <nav className="crumbs" aria-label="Breadcrumb" data-testid="breadcrumbs">
              {breadcrumbs.map((c, i) => (
                <React.Fragment key={c.path}>
                  {i > 0 && <ChevronRight className="sep" size={13} />}
                  {i < breadcrumbs.length - 1
                    ? <Link to={c.path}>{c.name}</Link>
                    : <span className="current" aria-current="page">{c.name}</span>}
                </React.Fragment>
              ))}
            </nav>
            <p className="eyebrow">{data.eyebrow}</p>
            <h1>{data.h1}</h1>
            <p className="lede">{data.intro}</p>
            <div className="about-cta">
              <a className="btn btn-gold" href={company.phoneHref}><Phone size={15} /> Call {company.phoneDisplay}</a>
              <Link className="btn btn-outline-dark" to="/contact"><Mail size={16} /> Email Us</Link>
            </div>
          </div>
          <div id="request-service">
            <ServiceLeadForm service={data.eyebrow} />
          </div>
        </div>
      </section>

      <section className="landing-body">
        <div className="wrap">
          <div className="landing-grid">
            <div>
              <h2>What&rsquo;s included</h2>
              <div className="val-grid">
                {data.included.map((s) => (
                  <div className="val-item" key={s}><span className="ic"><Check size={18} /></span><span>{s}</span></div>
                ))}
              </div>
            </div>
            <aside className="landing-side">
              <img src={data.image} alt={data.h1} loading="lazy" />
              <div className="landing-signs">
                <h3>{data.signsTitle}</h3>
                <ul className="sign-list">
                  {data.signs.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
            </aside>
          </div>

          <div className="landing-areas">
            <h3>Proudly serving Savannah &amp; the Coastal Empire</h3>
            <div className="area-chip-grid">
              {areas.map((a) => <div className="area-chip" key={a}><MapPin size={15} />{a}</div>)}
            </div>
          </div>

          <div className="blog-cta">
            <div>
              <h3>Ready to get started?</h3>
              <p>Call now or send a message &mdash; we&rsquo;ll get you scheduled fast.</p>
            </div>
            <div className="blog-cta-actions">
              <a className="btn btn-gold" href={company.phoneHref}><Phone size={16} /> Call {company.phoneDisplay}</a>
              <Link className="btn btn-outline-dark" to="/contact"><Mail size={16} /> Request Service</Link>
            </div>
          </div>
        </div>
      </section>

      {base === '/commercial' && (
        <section className="proj-strip-section" data-testid="project-gallery-strip">
          <div className="wrap">
            <div className="sec-head" style={{ marginBottom: '26px' }}>
              <p className="eyebrow">Recent Project Work</p>
              <h2>Real work across the Coastal Empire</h2>
              <p>A look at completed construction and repipe projects from our team in and around Savannah.</p>
            </div>
            <div className="proj-strip">
              {projectPhotos.map((p) => (
                <Link to="/gallery" className="proj-shot" key={p.img} data-testid="project-strip-photo">
                  <img src={p.img} alt={p.title} loading="lazy" />
                  <span className="proj-cap">{p.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {slug === 'contractor-services' && (
        <section className="trade-partner" data-testid="trade-partner-cta">
          <div className="wrap">
            <div className="trade-card">
              <div className="trade-copy">
                <span className="trade-badge"><Handshake size={18} /> For Builders &amp; General Contractors</span>
                <h3>Become a Trade Partner</h3>
                <p>Looking for a reliable plumbing subcontractor for your builds and remodels? Partner with Victory Plumbing for dependable rough-ins, finish plumbing, permits, and on-time scheduling &mdash; backed by a Class II Master Plumber and a standardized safety process.</p>
              </div>
              <div className="trade-actions">
                <button className="btn btn-gold" onClick={() => setTpOpen(true)} data-testid="become-trade-partner-btn">Become a Trade Partner <ArrowRight size={16} /></button>
                <a className="btn btn-outline-light" href={company.phoneHref}><Phone size={15} /> {company.phoneDisplay}</a>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="svc-reviews" data-testid="service-reviews">
        <div className="wrap">
          <div className="sec-head" style={{ marginBottom: '26px' }}>
            <p className="eyebrow">Reviews</p>
            <h2>What Savannah neighbors say</h2>
            <p>Real, verified 5-star reviews from Google &mdash; the same care goes into every job.</p>
          </div>
          <div className="svc-reviews-grid">
            {reviewCards.map((t, i) => (
              <figure className="svc-review" key={`${t.who}-${i}`} data-testid="service-review-card">
                <div className="svc-review-stars" aria-label={`${t.stars || 5} out of 5 stars`}>
                  {Array.from({ length: Math.round(t.stars || 5) }).map((_, s) => <Star key={s} size={15} fill="currentColor" strokeWidth={0} />)}
                </div>
                <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption><span className="svc-review-who">{t.who}</span><span className="svc-review-role">{t.role}</span></figcaption>
              </figure>
            ))}
          </div>
          <div className="svc-reviews-cta">
            <a className="btn btn-outline-dark" href={reviewsUrl} target="_blank" rel="noopener noreferrer">Read more Google reviews <ArrowRight size={14} /></a>
          </div>
        </div>
      </section>

      <PageFaq title={`${data.eyebrow} FAQs`} subtitle="Answers to the questions we hear most about this service." items={data.faqs} testid="service-landing-faqs" />
      <TradePartnerModal open={tpOpen} onClose={() => setTpOpen(false)} />

      <StickyBookBar bookHref="#request-service" bookLabel="Request Service" />
    </main>
  );
}
